import { useActorRef, useSelector } from '@xstate/react';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { ActorRefFrom, SnapshotFrom } from 'xstate';
import { UserContext } from '~/app';
import { responseUnmaskMachine } from './responseUnmask.machine';
import {
  responseUnmaskSessionMachine,
  type ResponseUnmaskOwner,
} from './responseUnmaskSession.machine';
import { selectUnmaskViewerScope } from './responseViewerScope';

const Context = createContext<{
  viewerScopeKey: string;
  actor: ActorRefFrom<typeof responseUnmaskSessionMachine>;
} | null>(null);
const selectResponses = (snapshot: SnapshotFrom<typeof responseUnmaskSessionMachine>) =>
  snapshot.context.responses;
const selectNotice = (snapshot: SnapshotFrom<typeof responseUnmaskMachine>) => {
  if (snapshot.context.attached) return null;
  if (snapshot.matches('mutating') || snapshot.context.recovering)
    return 'An identity-sharing action is still being checked.';
  if (snapshot.context.failedConsent)
    return 'An identity-sharing action has an unconfirmed outcome.';
  if (snapshot.context.confirmedAction && snapshot.context.error)
    return 'An identity-sharing action succeeded, but current permissions are unavailable.';
  return null;
};
const RecoveryNotice = ({
  responseId,
  owner,
}: {
  responseId: number;
  owner: ResponseUnmaskOwner;
}) => {
  const notice = useSelector(owner.actor, selectNotice);
  if (!notice || !owner.sourcePath) return null;
  return (
    <div className="m-4 space-y-2">
      <p role="status">{notice}</p>
      <Link className="text-primary underline" to={owner.sourcePath}>
        Review identity action for response {responseId}
      </Link>
    </div>
  );
};
const Owner = ({
  children,
  viewerScopeKey,
  recoveryLinks,
}: {
  children: ReactNode;
  viewerScopeKey: string;
  recoveryLinks: boolean;
}) => {
  const actor = useActorRef(responseUnmaskSessionMachine);
  const responses = useSelector(actor, selectResponses);
  return (
    <Context.Provider value={{ viewerScopeKey, actor }}>
      {recoveryLinks &&
        Object.entries(responses).map(([id, owner]) => (
          <RecoveryNotice key={id} responseId={Number(id)} owner={owner} />
        ))}
      {children}
    </Context.Provider>
  );
};
export const ResponseUnmaskBoundary = ({
  children,
  viewerScopeKey,
  recoveryLinks = false,
}: {
  children: ReactNode;
  viewerScopeKey: string;
  recoveryLinks?: boolean;
}) => {
  const parent = useContext(Context);
  return parent?.viewerScopeKey === viewerScopeKey ? (
    children
  ) : (
    <Owner key={viewerScopeKey} viewerScopeKey={viewerScopeKey} recoveryLinks={recoveryLinks}>
      {children}
    </Owner>
  );
};
export const ResponseUnmaskWorkspaceBoundary = ({ children }: { children: ReactNode }) => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  return (
    <ResponseUnmaskBoundary viewerScopeKey={viewerScopeKey} recoveryLinks>
      {children}
    </ResponseUnmaskBoundary>
  );
};
export const useResponseUnmaskActor = (responseId: number, sourcePath?: string) => {
  const owner = useContext(Context);
  if (!owner) throw new Error('Identity actions require their viewer boundary.');
  const actor = owner.actor;
  const response = useSelector(actor, (snapshot) => snapshot.context.responses[responseId]);
  useEffect(() => {
    actor.send({ type: 'OPEN_RESPONSE', responseId, sourcePath });
  }, [actor, responseId, sourcePath]);
  return response?.actor;
};
